import { WeibullDeclineReason } from '@Types/deteriorationModels.types';
import type { WeibullFallbackSeverity } from './WeibullFallbackBanner';

export interface WeibullDeclineNote {
	severity: WeibullFallbackSeverity;
	message: string;
}

// Internal shape only. The contract callers rely on is documented on the exported functions
// below, which is where a hover actually resolves.
interface DeclineDescription {
	severity: WeibullFallbackSeverity;
	// Deferred so the severity can be read without paying for an i18next lookup. The results table
	// asks for tone once per selected row per render; only the two surfaces that draw the sentence
	// call this.
	render: (t: (key: string) => string) => string;
}

const describeDecline = (reason: WeibullDeclineReason | undefined): DeclineDescription | null => {
	switch (reason) {
		case WeibullDeclineReason.NO_MINIMUM_LIFE:
			// The only decline that is not a problem: the fit succeeded and reported that the data
			// has no minimum life, so the 3-parameter curve it would draw is the 2-parameter one.
			// Stated, not flagged — see the `info` severity on WeibullFallbackBanner.
			return {
				severity: 'info',
				render: t =>
					t(
						// eslint-disable-next-line quotes
						"This asset group's failure data has no minimum life to estimate, so a 3-parameter curve would be identical to the 2-parameter one.",
					),
			};
		case WeibullDeclineReason.LOCATION_ON_BOUNDARY:
			// Almost always a data-quality problem rather than a property of the pipes: commonly a
			// placeholder survey date across a group's inspections, which collapses every failure age
			// onto one value. Worth naming so the user can go and look. See I360A-2882 for the
			// measurements behind that claim.
			return {
				severity: 'warning',
				render: t =>
					t(
						'The youngest failures in this asset group all share the same age, so a minimum life cannot be separated ' +
							'from it and the 2-parameter Weibull curve is used instead. This often means inspection or installation ' +
							'dates are missing from the source data.',
					),
			};
		case WeibullDeclineReason.INSUFFICIENT_FAILURES:
			// Reaches the user only through the no-curve-fitted banner. Both trainers share one
			// failure-count minimum, so this reason always arrives with zero fits and there is never
			// a 2-parameter curve to fall back to -- hence no claim that one was used, unlike its
			// siblings, and hence the explanation of what "usable" excludes.
			return {
				severity: 'warning',
				render: t =>
					t(
						'This asset group does not have enough usable failures to fit a curve. A failure is only usable when the ' +
							'asset has an installation date and a recorded failure date after it, so missing or placeholder ' +
							'dates reduce the count below the number of failures shown.',
					),
			};
		case WeibullDeclineReason.IMPLAUSIBLE_SHAPE:
			return {
				severity: 'warning',
				render: t =>
					t(
						'The 3-parameter fit for this asset group produced a step change rather than a deterioration curve, so the 2-parameter Weibull curve is used instead.',
					),
			};
		case WeibullDeclineReason.FIT_FAILED:
			return {
				severity: 'warning',
				render: t =>
					t(
						'The 3-parameter fit for this asset group did not produce usable coefficients, so the 2-parameter Weibull curve is used.',
					),
			};
		default:
			return null;
	}
};

/**
 * The tone a decline earns, without building its sentence. Callers that only need to colour
 * something — the results table asks per selected row on every render — should use this rather
 * than discarding a fully translated ~200-character string to read one field.
 */
export const getWeibullDeclineSeverity = (reason: WeibullDeclineReason | undefined): WeibullFallbackSeverity | null =>
	describeDecline(reason)?.severity ?? null;

/**
 * The message and tone for one backend decline reason (I360A-2882).
 *
 * Each message is one complete, self-contained sentence, so the configuration preview and the
 * results table show the same words to a user moving between them — the same single-home-for-
 * wording reasoning as `WeibullFitNotes`.
 *
 * **Only render these for a declined 3-parameter formulation.** Every message names the
 * 3-parameter fit, `declines` is keyed by formulation, and nothing here checks which key a reason
 * arrived under — the guarantee lives in each caller's render gate. `getWeibullDeclineSeverity`
 * below is equally formulation-blind, so a colour taken from it needs the same gate as a sentence.
 * Only the 3p trainer records a reason today, but that is a property of the trainer module rather
 * than of the contract, so do not lean on it.
 *
 * Where a message names the 2-parameter curve it says it is *used*, never that it is *shown*.
 * "Used" describes what the model did and stays true wherever it is rendered; "shown" is a claim
 * about the screen, and the results table renders these in a tooltip on rows that are not plotted.
 * `insufficient_failures` makes no such claim at all — see its case above.
 *
 * Deliberately not assembled from a shared lead plus a per-reason clause. Concatenating two
 * separately-translated strings hands the join to code that cannot know the target language's
 * conventions: Japanese ends a sentence with `。` and takes no space after it, so an ASCII
 * separator was already producing `。 `. One string per message keeps the whole sentence,
 * punctuation included, inside the translation unit where a translator can fix it.
 *
 * Returns `null` for a reason that is absent or that this build does not know about. Both mean
 * "unspecified", and the caller must keep its existing generic wording: groups trained before
 * the backend change carry no reason, and the backend can add a sixth without a frontend
 * release. Deliberately not a total `Record` over the enum — a missing entry has to degrade,
 * not fail to compile in a build that has already shipped.
 */
export const getWeibullDeclineNote = (
	reason: WeibullDeclineReason | undefined,
	t: (key: string) => string,
): WeibullDeclineNote | null => {
	const described = describeDecline(reason);
	return described ? { severity: described.severity, message: described.render(t) } : null;
};
